import { useCallback, useEffect, useRef, useState } from 'react';
import {
  REFERENCE_BANK_VIEWS,
  type ReferenceBankEntry,
  type ReferenceBankView,
} from '../types';
import {
  createReferenceBankEntry,
  deleteReferenceBankEntry,
  fetchReferenceBank,
  fetchReferenceBankViewObjectUrl,
  updateReferenceBankEntry,
} from '../hooks/useSettings';
import { createLogger } from '../utils/logger';
import { AdminAction } from './PermissionGate';

const log = createLogger('ReferenceBankTab');

const VIEW_LABELS: Record<ReferenceBankView, string> = {
  left: 'Left side',
  right: 'Right side',
  front_left: 'Front-left 3/4',
  front_right: 'Front-right 3/4',
  rear_left: 'Rear-left 3/4',
  rear_right: 'Rear-right 3/4',
  front: 'Front',
  rear: 'Rear',
};

type ViewFiles = Partial<Record<ReferenceBankView, File>>;

interface FormState {
  make: string;
  model: string;
  trim: string;
  yearFrom: string;
  yearTo: string;
}

const EMPTY_FORM: FormState = { make: '', model: '', trim: '', yearFrom: '', yearTo: '' };

function formatYears(entry: ReferenceBankEntry): string {
  return entry.year_from === entry.year_to
    ? String(entry.year_from)
    : `${entry.year_from}–${entry.year_to}`;
}

function parseYear(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d{4}$/.test(trimmed)) return null;
  const year = Number(trimmed);
  return year >= 1900 && year <= 2100 ? year : null;
}

function ReferenceViewThumbnail({
  entryId,
  view,
  version,
}: {
  entryId: string;
  view: ReferenceBankView;
  version: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setFailed(false);
    fetchReferenceBankViewObjectUrl(entryId, view, version)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      })
      .catch((err) => {
        if (cancelled) return;
        log.warn(`Failed to load reference view ${entryId}/${view}`, err);
        setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entryId, view, version]);

  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center text-[10px] text-surface-400">
        Unavailable
      </div>
    );
  }
  if (!src) return null;
  return <img src={src} alt={VIEW_LABELS[view]} className="w-full h-full object-contain" />;
}

function LocalFilePreview({ file, alt }: { file: File; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!src) return null;
  return <img src={src} alt={alt} className="w-full h-full object-contain" />;
}

function ViewSlot({
  view,
  file,
  existing,
  onSelect,
  onClear,
}: {
  view: ReferenceBankView;
  file: File | undefined;
  existing: { entryId: string; version: string } | null;
  onSelect: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasImage = Boolean(file || existing);

  return (
    <div
      className={`rounded-lg border overflow-hidden bg-white ${
        hasImage ? 'border-surface-200' : 'border-dashed border-surface-300'
      }`}
    >
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="block w-full aspect-[4/3] bg-surface-50 p-1.5 hover:bg-surface-100 transition-colors"
        title={`Choose ${VIEW_LABELS[view]} image`}
      >
        {file ? (
          <LocalFilePreview file={file} alt={VIEW_LABELS[view]} />
        ) : existing ? (
          <ReferenceViewThumbnail entryId={existing.entryId} view={view} version={existing.version} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-surface-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-xs">Upload</span>
          </div>
        )}
      </button>
      <div className="px-2 py-1.5 flex items-center justify-between gap-1 border-t border-surface-100">
        <span className="text-xs font-medium text-surface-700 truncate">{VIEW_LABELS[view]}</span>
        {file ? (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] text-surface-400 hover:text-danger"
            title={existing ? 'Keep the current image' : 'Remove'}
          >
            {existing ? 'Undo' : 'Clear'}
          </button>
        ) : existing ? (
          <span className="text-[11px] text-surface-400">Saved</span>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={(event) => {
          const selected = event.target.files?.[0];
          if (selected) onSelect(selected);
          event.target.value = '';
        }}
      />
    </div>
  );
}

export function ReferenceBankTab() {
  const [entries, setEntries] = useState<ReferenceBankEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [files, setFiles] = useState<ViewFiles>({});
  const [editing, setEditing] = useState<ReferenceBankEntry | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setEntries(await fetchReferenceBank());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reference bank');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFiles({});
    setEditing(null);
  };

  const startEdit = (entry: ReferenceBankEntry) => {
    setEditing(entry);
    setForm({
      make: entry.make,
      model: entry.model,
      trim: entry.trim,
      yearFrom: String(entry.year_from),
      yearTo: String(entry.year_to),
    });
    setFiles({});
    setMessage(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const yearFrom = parseYear(form.yearFrom);
  const yearTo = form.yearTo.trim() ? parseYear(form.yearTo) : yearFrom;
  const yearsValid = yearFrom !== null && yearTo !== null && yearFrom <= yearTo;
  const textValid = Boolean(form.make.trim() && form.model.trim() && form.trim.trim());
  const missingViews = REFERENCE_BANK_VIEWS.filter(
    (view) => !files[view] && !editing?.views.includes(view),
  );
  const canSubmit = textValid && yearsValid && missingViews.length === 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit || yearFrom === null || yearTo === null) return;
    const fields = {
      make: form.make.trim(),
      model: form.model.trim(),
      trim: form.trim.trim(),
      year_from: yearFrom,
      year_to: yearTo,
    };
    try {
      setSaving(true);
      setError(null);
      setMessage(null);
      if (editing) {
        await updateReferenceBankEntry(editing.id, fields, files);
        setMessage(`Updated ${fields.make} ${fields.model} ${fields.trim}`);
      } else {
        await createReferenceBankEntry(fields, files as Record<ReferenceBankView, File>);
        setMessage(`Added ${fields.make} ${fields.model} ${fields.trim}`);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save reference bank entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry: ReferenceBankEntry) => {
    const label = `${entry.make} ${entry.model} ${entry.trim} (${formatYears(entry)})`;
    if (!confirm(`Delete reference "${label}"?`)) return;
    try {
      setError(null);
      await deleteReferenceBankEntry(entry.id);
      if (editing?.id === entry.id) resetForm();
      await load();
      setMessage(`Deleted ${label}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete reference bank entry');
    }
  };

  const textInput = (key: keyof FormState, label: string, placeholder: string, inputMode?: 'numeric') => (
    <div>
      <label className="block text-xs font-medium text-surface-600 mb-1">{label}</label>
      <input
        type="text"
        inputMode={inputMode}
        value={form[key]}
        onChange={(event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))}
        placeholder={placeholder}
        maxLength={inputMode ? 4 : 128}
        className="w-full px-3 py-2 bg-white border border-surface-200 rounded-lg text-sm"
      />
    </div>
  );

  return (
    <section className="bg-white rounded-xl border border-surface-200 p-6 shadow-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-surface-800">Reference Bank</h2>
          <p className="hidden md:block text-sm text-surface-500">
            Canonical model references used during merch generation when a scan matches make, model, year and trim exactly
          </p>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-danger-light text-danger-dark text-sm">{error}</div>}
      {message && <div className="mb-4 p-3 rounded-lg bg-success-light text-success-dark text-sm">{message}</div>}

      <div className="p-4 bg-surface-50 rounded-lg border border-surface-100 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-surface-800">
            {editing ? `Edit ${editing.make} ${editing.model} ${editing.trim}` : 'New reference'}
          </h3>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="text-xs text-surface-500 hover:text-surface-700"
            >
              Cancel edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {textInput('make', 'Make', 'e.g. Audi')}
          {textInput('model', 'Model', 'e.g. A5')}
          {textInput('trim', 'Trim', 'e.g. Premium Plus')}
          {textInput('yearFrom', 'Year from', '2025', 'numeric')}
          {textInput('yearTo', 'Year to', form.yearFrom || '2026', 'numeric')}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {REFERENCE_BANK_VIEWS.map((view) => (
            <ViewSlot
              key={view}
              view={view}
              file={files[view]}
              existing={
                editing?.views.includes(view)
                  ? { entryId: editing.id, version: editing.updated_at }
                  : null
              }
              onSelect={(file) => setFiles((prev) => ({ ...prev, [view]: file }))}
              onClear={() =>
                setFiles((prev) => {
                  const next = { ...prev };
                  delete next[view];
                  return next;
                })
              }
            />
          ))}
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-surface-400">
            All 8 views are required (PNG or JPEG; larger than 2048px is downscaled). Trim must match the
            detected trim exactly (case-insensitive). Leave “Year to” empty for a single year.
            {!yearsValid && (form.yearFrom || form.yearTo) && (
              <span className="block text-danger mt-1">Years must be 1900–2100 and “from” ≤ “to”.</span>
            )}
            {missingViews.length > 0 && missingViews.length < REFERENCE_BANK_VIEWS.length && (
              <span className="block mt-1">
                Missing: {missingViews.map((view) => VIEW_LABELS[view]).join(', ')}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="shrink-0 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Add reference'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-surface-500 text-center py-8">Loading reference bank...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-surface-500 text-center py-8">No reference entries yet</p>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className={`rounded-lg border p-4 ${
                editing?.id === entry.id ? 'border-primary-300 bg-primary-50/40' : 'border-surface-200'
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-surface-800 truncate">
                    {entry.make} {entry.model}
                    <span className="ml-2 px-2 py-0.5 rounded bg-surface-100 text-surface-600 text-xs font-medium">
                      {entry.trim}
                    </span>
                  </div>
                  <div className="text-xs text-surface-500 mt-0.5">
                    {formatYears(entry)}
                    {entry.created_by && <> · added by {entry.created_by}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(entry)}
                    title="Edit"
                    className="p-1.5 rounded-md text-surface-400 hover:text-primary-700 hover:bg-primary-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <AdminAction>
                    <button
                      type="button"
                      onClick={() => handleDelete(entry)}
                      title="Delete"
                      className="p-1.5 rounded-md text-surface-400 hover:text-danger hover:bg-danger-light"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4h-4v3M4 7h16" />
                      </svg>
                    </button>
                  </AdminAction>
                </div>
              </div>
              <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
                {REFERENCE_BANK_VIEWS.map((view) => (
                  <div key={view}>
                    <div className="aspect-[4/3] rounded bg-surface-100 p-1">
                      {entry.views.includes(view) ? (
                        <ReferenceViewThumbnail entryId={entry.id} view={view} version={entry.updated_at} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-danger">
                          Missing
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-surface-500 mt-1 truncate text-center">{VIEW_LABELS[view]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
